import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { I18n } from 'common/i18n';
import SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';
import { chooseNetTotalGroup, netTotalFieldNormalize } from 'common/visualizations/helpers/NetTotalHelpers';
import { Hierarchy } from 'common/visualizations/vif';

import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import { getCurrentVif, getTableHierarchies } from 'common/authoring_workflow/selectors/vifAuthoring';
import { setNetTotalCalc } from 'common/authoring_workflow/actions';
import { getSoqlDataProviderConfigFromVif } from 'common/visualizations/dataProviders/DataProviderHelper';

const scope = 'shared.visualizations.panes.data.fields.table_hierarchies.net_total';

export interface NetTotalProps {
  hierarchyIndex: number;
}

// Remove/simplify this entire component with `enable_prototype_net_total`
const NetTotalEditor = (props: NetTotalProps) => {
  const { hierarchyIndex } = props;

  const dispatch = useDispatch();

  const dataProviderConfig = useSelector((state: AuthoringWorkflowState) => {
    return getSoqlDataProviderConfigFromVif(getCurrentVif(state.vifAuthoring));
  }, isEqual);

  const soqlDataProvider = useMemo(
    () => new SoqlDataProvider(dataProviderConfig, true),
    [dataProviderConfig],
  );

  const currentHierarchy = useSelector((state: AuthoringWorkflowState) => {
    const hierarchies: Hierarchy[] = getTableHierarchies(state.vifAuthoring);
    return hierarchies[hierarchyIndex];
  }, isEqual);

  const netTotalConfig = chooseNetTotalGroup(currentHierarchy.columnConfigurations);
  const groupName = netTotalConfig?.columnName;
  const calcFromVif = netTotalConfig?.netTotalCalc;

  const [netTotalInputOpen, setNetTotalInputOpen] = useState(false);
  const placeholderText = groupName ? I18n.t('loading', { scope }) : I18n.t('no_group', { scope });
  const [localCalc, setLocalCalc] = useState(calcFromVif || placeholderText);

  const shouldSkipFirstQuery = React.useRef(Boolean(calcFromVif));

  const buttonText = netTotalInputOpen ? I18n.t('save', { scope }) : I18n.t('edit', { scope });

  // Any time the first group changes, we query the distinct values for that group.
  useEffect(() => {
    const fetchRowGroups = async () => {
      if (shouldSkipFirstQuery.current) {
        // No need to query on first render if we already have a saved calc.
        shouldSkipFirstQuery.current = false;
        return;
      }

      if (!groupName) {
        // If there's no group, net totals is basically disabled
        setLocalCalc(placeholderText);
        return;
      }

      const rawGroups = await queryGroups(soqlDataProvider, groupName);
      const normalizedGroups = rawGroups.map(netTotalFieldNormalize);
      // TODO: We could check for existing calc instead of resetting, but then need dedicated reset button
      setLocalCalc(normalizedGroups.join(' + '));
    };
    fetchRowGroups();
    // Very important not to re-run the useEffect unless we actually need to,
    // since it both queries and includes the reset functionality.
  }, [soqlDataProvider, groupName, placeholderText]);

  const handleButtonClick = () => {
    if (netTotalInputOpen && groupName && localCalc !== calcFromVif) {
      // User just closed the input, so we want to save the calc.
      dispatch(setNetTotalCalc(hierarchyIndex, localCalc, groupName));
    }
    setNetTotalInputOpen(!netTotalInputOpen);
  };

  return (
    <div className="net-total-container">
      <div className="net-total-button-container">
        <button className="net-total-button" onClick={handleButtonClick}>
          {buttonText}
        </button>
      </div>
      {netTotalInputOpen && (
        <div className="net-total-input-container">
          <code>
            <textarea
              className="net-total-input"
              value={localCalc}
              onChange={(e) => setLocalCalc(e.target.value)}
              spellCheck="false"
            />
          </code>
        </div>
      )}
    </div>
  );
};

export default NetTotalEditor;

const queryGroups = async (soqlDataProvider: SoqlDataProvider, groupName: string) => {
  const distinctGroups: { [groupName: string]: string }[] = await soqlDataProvider.invokeSoqlQuery(
    () => `select distinct \`${groupName}\``,
    [],
  );
  const groupNames = distinctGroups.map((group) => group[groupName]);
  return groupNames;
};
